import { useEffect, useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Edit, RefreshCw, ArrowUpDown, ArrowUp, ArrowDown, FileText } from "lucide-react";
import EditableCell from "./EditableCell";
import {
  useGetAccountsQuery,
  useUpdateAccountMutation,
  useUpdateAccountSubscriptionMutation,
  useCancelSubscriptionMutation,
  type Account,
} from "@/redux/api/accounts.rtkq";
import { useGetPlansQuery, useRestorePlanMutation } from "@/redux/api/plans.rtkq";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { subscriptionFormSchema, type SubscriptionFormValues } from "@/lib/forms/subscriptionFormSchema";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { ProfileForm } from "@/components/profile/ProfileForm";
import { toast } from "sonner";
import PlanAssignment from "./PlanAssignment";
import PlanNativeSelect from "./PlanNativeSelect";
import { useUpdateAdminOfficeMutation } from "@/redux/api/adminOffices.rtkq";

type SortField = "name" | "owner" | "plan" | "billing" | "status" | "credits" | "createdAt" | "lastUsageDate";
type SortDirection = "asc" | "desc";

const BILLING_STATUS_OPTIONS = [
  { value: "paying", label: "משלם" },
  { value: "trial", label: "ניסיון" },
  { value: "courtesy", label: "courtesy" },
  { value: "none", label: "אין" },
];

const billingLabel = (status?: string): string =>
  BILLING_STATUS_OPTIONS.find((o) => o.value === status)?.label || status || "אין";

const AccountsManagement = () => {
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isAccountInfoDialogOpen, setIsAccountInfoDialogOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState<Account | null>(null);
  const [sortField, setSortField] = useState<SortField>("createdAt");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");

  // RTK Query hooks
  const {
    data: accounts = [],
    isLoading: accountsLoading,
    error: accountsError,
    refetch: refetchAccounts,
  } = useGetAccountsQuery();
  const { data: plans = [], isLoading: plansLoading } = useGetPlansQuery();
  const [restorePlan, { isLoading: isRestoringPlan }] = useRestorePlanMutation();
  const [
    updateAccountSubscription,
    {
      isLoading: isUpdatingSubscription,
      isSuccess: isSubscriptionSuccess,
      isError: isSubscriptionError,
      error: subscriptionError,
    },
  ] = useUpdateAccountSubscriptionMutation();
  const [updateAccount] = useUpdateAccountMutation();
  const [updateAdminOffice] = useUpdateAdminOfficeMutation();
  const [
    cancelSubscription,
    { isLoading: isCancellingSubscription, isSuccess: isCancelSuccess, isError: isCancelError, error: cancelError },
  ] = useCancelSubscriptionMutation();

  // Sorting logic
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection(field === "lastUsageDate" ? "desc" : "asc");
    }
  };

  const sortedAccounts = useMemo(() => {
    const sorted = [...accounts].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortField) {
        case "name":
          aValue = a.name?.toLowerCase() || "";
          bValue = b.name?.toLowerCase() || "";
          break;
        case "owner":
          aValue = a.owner?.displayName?.toLowerCase() || a.owner?.email?.toLowerCase() || "";
          bValue = b.owner?.displayName?.toLowerCase() || b.owner?.email?.toLowerCase() || "";
          break;
        case "plan":
          aValue = a.subscription?.plan?.name?.toLowerCase() || "";
          bValue = b.subscription?.plan?.name?.toLowerCase() || "";
          break;
        case "billing":
          aValue = a.billingStatus || "";
          bValue = b.billingStatus || "";
          break;
        case "status":
          aValue = a.subscription?.status || "";
          bValue = b.subscription?.status || "";
          break;
        case "credits":
          aValue = a.creditBalance || 0;
          bValue = b.creditBalance || 0;
          break;
        case "createdAt":
          aValue = a.createdAt ? (a.createdAt instanceof Date ? a.createdAt.getTime() : new Date(a.createdAt).getTime()) : 0;
          bValue = b.createdAt ? (b.createdAt instanceof Date ? b.createdAt.getTime() : new Date(b.createdAt).getTime()) : 0;
          break;
        case "lastUsageDate":
          // "ללא שימוש" (no usage) should always be at the bottom when sorting
          aValue = a.lastUsageDate
            ? a.lastUsageDate instanceof Date
              ? a.lastUsageDate.getTime()
              : new Date(a.lastUsageDate).getTime()
            : 0;
          bValue = b.lastUsageDate
            ? b.lastUsageDate instanceof Date
              ? b.lastUsageDate.getTime()
              : new Date(b.lastUsageDate).getTime()
            : 0;
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [accounts, sortField, sortDirection]);

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) return <ArrowUpDown className="h-4 w-4 opacity-50" />;
    return sortDirection === "asc" ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />;
  };

  // Helper function to safely format dates with error handling
  const formatDate = (date: Date | string | undefined, fallback: string = "—"): string => {
    if (!date) return fallback;

    try {
      const dateObj = date instanceof Date ? date : new Date(date);

      // Validate that the date is valid
      if (isNaN(dateObj.getTime())) {
        console.error("Invalid date value:", date);
        return "תאריך לא תקין";
      }

      return dateObj.toLocaleDateString("he-IL", { day: "2-digit", month: "2-digit", year: "numeric" });
    } catch (error) {
      console.error("Error formatting date:", date, error);
      return "תאריך לא תקין";
    }
  };

  useEffect(() => {
    if (isSubscriptionSuccess) {
      toast.success("תוכנית המנוי עודכנה בהצלחה");
    }

    if (isSubscriptionError) {
      toast.error(`שגיאה בעדכון תוכנית המנוי: ${(subscriptionError as any)?.message || "שגיאה לא ידועה"}`);
    }
  }, [isSubscriptionSuccess, isSubscriptionError, subscriptionError]);

  useEffect(() => {
    if (isCancelSuccess) {
      toast.success("המנוי בוטל בהצלחה");
    }

    if (isCancelError) {
      toast.error(`שגיאה בביטול המנוי: ${(cancelError as any)?.message || "שגיאה לא ידועה"}`);
    }
  }, [isCancelSuccess, isCancelError, cancelError]);

  // Subscription form
  const subscriptionForm = useForm<SubscriptionFormValues>({
    resolver: zodResolver(subscriptionFormSchema),
    defaultValues: {
      planId: "",
    },
  });

  const handleUpdateSubscription = async (data: SubscriptionFormValues) => {
    if (!editingAccount || !data.planId) return;

    const selectedSubscriptionPlan = plans.find((p) => p.planId === data.planId);
    if (!selectedSubscriptionPlan) return;

    try {
      await updateAccountSubscription({
        accountId: editingAccount.accountId,
        newPlanId: data.planId,
      }).unwrap();

      // Manually refetch accounts to ensure UI updates
      setEditingAccount(null);
      subscriptionForm.reset();
      setIsEditDialogOpen(false);
    } catch (error) {
      console.error("Error updating subscription:", error);
    }
  };

  const handleCancelSubscription = async () => {
    if (!editingAccount) return;

    try {
      await cancelSubscription({
        accountId: editingAccount.accountId,
        immediate: false, // Cancel at period end by default
        cancelledBy: "admin",
        reason: "Cancelled by admin from accounts management",
      }).unwrap();

      setEditingAccount(null);
      subscriptionForm.reset();
      setIsEditDialogOpen(false);
    } catch (error) {
      console.error("Error cancelling subscription:", error);
    }
  };

  const openEditDialog = (account: Account) => {
    setEditingAccount(account);
    subscriptionForm.reset({
      planId: account.subscription?.plan?.planId ?? "",
    });
    setIsEditDialogOpen(true);
  };

  const openAccountInfoDialog = (account: Account) => {
    setEditingAccount(account);
    setIsAccountInfoDialogOpen(true);
  };

  const handleAccountInfoSuccess = () => {
    toast.success("פרטי החשבון עודכנו בהצלחה");
    setEditingAccount(null);
    setIsAccountInfoDialogOpen(false);
  };

  const handleAccountInfoError = (error: unknown) => {
    toast.error(
      `שגיאה בעדכון פרטי החשבון: ${
        error instanceof Error ? error.message : "שגיאה לא ידועה"
      }`
    );
  };

  const getStatusBadge = (status: string) => {
    const variants = {
      active: "default",
      past_due: "destructive",
      cancelled: "secondary",
      expired: "destructive",
      incomplete: "outline",
    } as const;

    const labels = {
      active: "פעיל",
      past_due: "תשלום באיחור",
      cancelled: "מבוטל",
      expired: "פג תוקף",
      incomplete: "לא הושלם",
    } as const;

    return (
      <Badge variant={variants[status as keyof typeof variants] || "secondary"}>
        {labels[status as keyof typeof labels] || status}
      </Badge>
    );
  };

  if ((accountsLoading && accounts.length === 0) || (plansLoading && plans.length === 0)) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-slate-600">טוען נתונים...</div>
      </div>
    );
  }

  if (accountsError) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-red-600">שגיאה בטעינת החשבונות</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-slate-900">ניהול חשבונות משתמשים</h3>
          <p className="text-sm text-slate-600">צפייה ועריכה של חשבונות ותוכניות מנוי. קרדיטים נערכים במשרדים לפי אפליקציה.</p>
        </div>
      </div>

      {/* Accounts Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>חשבונות במערכת</CardTitle>
              <CardDescription>רשימת כל החשבונות הפעילים במערכת</CardDescription>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetchAccounts()}
              disabled={accountsLoading}
              className="border-slate-200 text-slate-800 hover:bg-slate-100"
            >
              <RefreshCw className="h-4 w-4 ml-2" />
              {accountsLoading ? "מרענן..." : "רענן"}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {accounts.length === 0 ? (
            <div className="text-center py-8 text-slate-600">אין חשבונות זמינים במערכת.</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleSort("name")}
                      className="h-8 text-right flex items-center gap-2"
                    >
                      שם החשבון
                      {getSortIcon("name")}
                    </Button>
                  </TableHead>
                  <TableHead className="text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleSort("owner")}
                      className="h-8 text-right flex items-center gap-2"
                    >
                      בעלים
                      {getSortIcon("owner")}
                    </Button>
                  </TableHead>
                  <TableHead className="text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleSort("plan")}
                      className="h-8 text-right flex items-center gap-2"
                    >
                      תוכנית מנוי
                      {getSortIcon("plan")}
                    </Button>
                  </TableHead>
                  <TableHead className="text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleSort("billing")}
                      className="h-8 text-right flex items-center gap-2"
                    >
                      בילינג
                      {getSortIcon("billing")}
                    </Button>
                  </TableHead>
                  <TableHead className="text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleSort("status")}
                      className="h-8 text-right flex items-center gap-2"
                    >
                      סטטוס
                      {getSortIcon("status")}
                    </Button>
                  </TableHead>
                  <TableHead className="text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleSort("credits")}
                      className="h-8 text-right flex items-center gap-2"
                    >
                      קרדיטים זמינים
                      {getSortIcon("credits")}
                    </Button>
                  </TableHead>
                  <TableHead className="text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleSort("createdAt")}
                      className="h-8 text-right flex items-center gap-2"
                    >
                      תאריך יצירה
                      {getSortIcon("createdAt")}
                    </Button>
                  </TableHead>
                  <TableHead className="text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleSort("lastUsageDate")}
                      className="h-8 text-right flex items-center gap-2"
                    >
                      תאריך שימוש אחרון
                      {getSortIcon("lastUsageDate")}
                    </Button>
                  </TableHead>
                  <TableHead className="text-right">פעולות</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedAccounts.map((account) => (
                  <TableRow key={account.accountId}>
                    <TableCell className="text-right py-3">
                      <div className="flex items-center justify-between gap-3">
                        <EditableCell
                          value={account.name || ""}
                          onSave={async (val) => { await updateAccount({ accountId: account.accountId, updates: { name: val as string } }).unwrap(); }}
                          type="text"
                          displayValue={<span className="font-light">{account.name}</span>}
                        />
                        {account.accountInformation?.isProfileComplete ? (
                          <Badge variant="default" className="bg-green-600 text-xs shrink-0">
                            פרופיל מלא
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-xs text-orange-600 border-orange-600 shrink-0">
                            פרופיל חסר
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div>
                        <div className="font-medium">{account.owner?.displayName || account.ownerId || "—"}</div>
                        {account.owner?.email && <div className="text-xs text-muted-foreground">{account.owner.email}</div>}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <PlanAssignment
                        planId={account.subscription?.plan?.planId}
                        planName={account.subscription?.plan?.name}
                        planActive={account.subscription?.plan?.isActive}
                        plans={plans}
                        restoring={isRestoringPlan}
                        onRestore={(planId) => {
                          void restorePlan({ planId }).unwrap();
                        }}
                        onChange={(planId) => {
                          void updateAccountSubscription({
                            accountId: account.accountId,
                            newPlanId: planId,
                          }).unwrap();
                        }}
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <EditableCell
                        value={account.billingStatus || "none"}
                        onSave={async (val) => {
                          await updateAdminOffice({
                            id: account.accountId,
                            updates: { billingStatus: val as string },
                          }).unwrap();
                        }}
                        type="select"
                        options={BILLING_STATUS_OPTIONS}
                        displayValue={<Badge variant="outline">{billingLabel(account.billingStatus)}</Badge>}
                      />
                    </TableCell>
                    <TableCell className="text-right">{getStatusBadge(account.subscription?.status)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center gap-2">
                        <span className="font-mono">{account.creditBalance?.toLocaleString()}</span>
                        <Badge variant="secondary">קרדיטים</Badge>
                      </div>
                    </TableCell>
                    <TableCell className="text-right text-sm text-slate-600">{formatDate(account.createdAt)}</TableCell>
                    <TableCell className="text-right text-sm text-slate-600">
                      {formatDate(account.lastUsageDate, "ללא שימוש")}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center gap-2">
                        <Button variant="ghost" size="sm" onClick={() => openEditDialog(account)}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openAccountInfoDialog(account)}
                          aria-label="צפייה ועריכת מידע חשבון"
                        >
                          <FileText className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Edit Subscription Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>עריכת תוכנית מנוי</DialogTitle>
            <DialogDescription>שנה את תוכנית המנוי עבור {editingAccount?.name}</DialogDescription>
          </DialogHeader>
          <Form {...subscriptionForm}>
            <form onSubmit={subscriptionForm.handleSubmit(handleUpdateSubscription)} className="space-y-4">
              <FormField
                control={subscriptionForm.control}
                name="planId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>תוכנית מנוי</FormLabel>
                    <FormControl>
                      <PlanNativeSelect
                        className="h-9 w-full max-w-none"
                        value={field.value}
                        plans={plans}
                        placeholder="בחר תוכנית"
                        formatLabel={(plan) =>
                          `${plan.internalName} - ₪${plan.priceMonthly}/חודש`
                        }
                        onChange={field.onChange}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <DialogFooter className="flex-col sm:flex-row gap-2">
                <div className="flex gap-2 flex-1">
                  <Button variant="outline" type="button" onClick={() => setIsEditDialogOpen(false)}>
                    סגור
                  </Button>
                  <Button
                    variant="destructive"
                    type="button"
                    onClick={handleCancelSubscription}
                    disabled={
                      isCancellingSubscription ||
                      editingAccount?.subscription?.status === "cancelled" ||
                      editingAccount?.subscription?.status === "expired"
                    }
                  >
                    {isCancellingSubscription ? "מבטל..." : "ביטול מנוי"}
                  </Button>
                </div>
                <Button type="submit" disabled={isUpdatingSubscription}>
                  {isUpdatingSubscription ? "מעדכן..." : "עדכן תוכנית"}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* View/Edit Account Information Dialog */}
      <Dialog open={isAccountInfoDialogOpen} onOpenChange={setIsAccountInfoDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-right">מידע חשבון</DialogTitle>
            <DialogDescription className="text-right">
              צפייה ועריכת פרטי החשבון עבור {editingAccount?.name}
            </DialogDescription>
          </DialogHeader>

          {/* Profile Completion Status Badge */}
          <div className="mb-4">
            {editingAccount?.accountInformation?.isProfileComplete ? (
              <Badge variant="default" className="bg-green-600">
                פרופיל הושלם
              </Badge>
            ) : (
              <Badge variant="outline" className="text-orange-600 border-orange-600">
                פרופיל לא הושלם
              </Badge>
            )}
          </div>

          {/* ProfileForm Component */}
          {editingAccount && (
            <ProfileForm
              accountId={editingAccount.accountId}
              initialValues={{
                phoneNumber: editingAccount.accountInformation?.phoneNumber || "",
                businessName: editingAccount.accountInformation?.businessName || "",
                businessNumber: editingAccount.accountInformation?.businessNumber || "",
                city: editingAccount.accountInformation?.city || "",
              }}
              onSuccess={handleAccountInfoSuccess}
              onError={handleAccountInfoError}
              submitButtonText="שמור שינויים"
            />
          )}

          <DialogFooter className="mt-4">
            <Button
              variant="outline"
              onClick={() => setIsAccountInfoDialogOpen(false)}
            >
              סגור
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AccountsManagement;
